import { computed, onMounted, ref, watch } from 'vue';
import { useI18n, usePlanLimits } from '@/composables';
import { $emitter } from '@/services/emitter';
import { useStore } from '@/store';

type FlowPathCardPath = {
  landings: Array<{ id: string; weight?: number; offers?: number }>;
  offers: Array<{ id: string; weight: number }>;
  directLinking?: boolean;
  redirectMode?: string;
  redirectModeOffers?: string;
  retargetingLandings?: string;
  retargetingOffers?: string;
  redirectDomainId?: string;
  redirectDomainIdOffers?: string;
};

type FlowPathCardProps = {
  type: string;
  path: FlowPathCardPath;
  ruleIndex: number;
  pathIndex: number;
  isDirectLinking?: boolean;
};

const flowPathEvents = {
  addOfferEvent: 'flow-new-offer',
  editOfferEvent: 'flow-edit-offer',
  deleteOfferEvent: 'flow-delete-offer',
  addLandingEvent: 'flow-new-landing',
  editLandingEvent: 'flow-edit-landing',
  deleteLandingEvent: 'flow-delete-landing',
};

export const useFlowPathCard = (props: FlowPathCardProps) => {
  const { t } = useI18n();
  const store = useStore();
  const offersPlanLimits = usePlanLimits('offersAmount');
  const landingsPlanLimits = usePlanLimits('landingsAmount');
  const planLimits = usePlanLimits(undefined);

  const virtualScrollBreakpoint = 100;

  const redirectMode = ref('302');
  const retargeting = ref(false);
  const noDirect = ref(true);
  const retargetingValue = ref('');
  const redirectDomainId = ref('');

  const currentOffersCount = computed(() => props.path.offers.length);

  const offersCount = computed(() => {
    if (props.isDirectLinking) {
      return 0;
    }

    const landing = props.path.landings?.[0];
    const mainLanding = landing
      ? props.path.landings.find((item) => item.id === landing.id)
      : null;

    return mainLanding ? mainLanding.offers || 0 : 0;
  });

  const selections = computed(() =>
    props.type === 'landings' ? props.path.landings : props.path.offers,
  );

  const isAddDisabled = computed(() => offersCount.value > 1 && props.type === 'offers');

  const typeSingle = computed(() => props.type.substring(0, props.type.length - 1));

  const pathCardTitle = computed(() => {
    switch (props.type) {
      case 'landings':
        return t('landings');
      case 'offers':
        return t('offers');
      default:
        return '';
    }
  });

  const retargetingLabel = computed(() => {
    switch (props.type) {
      case 'landings':
        return t('Use retargeting for landings');
      case 'offers':
        return t('Use retargeting for offers');
      default:
        return '';
    }
  });

  const showPlanLimitsWarningDoubleMeta = computed(
    () =>
      redirectMode.value === 'doubleMeta'
      && planLimits.hasPlanLimitsRestriction('doubleMetaRedirect'),
  );

  const totalWeightOfLandings = computed(() =>
    props.path.landings
      .map((item) => +item.weight)
      .reduce((accumulator, item) => accumulator + item, 0),
  );

  const totalWeightOfOffers = computed(() =>
    props.path.offers
      .map((item) => +item.weight)
      .reduce((accumulator, item) => accumulator + item, 0),
  );

  const getGetOffer = (offerCount = 1) =>
    new Array(offerCount).fill(null).map(() => ({
      id: '',
      weight: 100,
    }));

  const addOffer = (offerCount = 1) => {
    const newOffers = getGetOffer(offerCount);
    props.path.offers.push(...newOffers);
  };

  const addNewOffer = () => {
    if (offersPlanLimits.hasPlanLimitsRestriction('offersAmount')) {
      return offersPlanLimits.handleClickPlanLimits('offersAmount');
    }

    $emitter.emit(flowPathEvents.addOfferEvent);
  };

  const editOffer = (id: string) => {
    $emitter.emit(flowPathEvents.editOfferEvent, id);
  };

  const previewOffer = (url: string) => {
    window.open(url, '_blank');
  };

  const delOffer = (offerIndex: number, count = 1) => {
    props.path.offers.splice(offerIndex, count);
    $emitter.emit(flowPathEvents.deleteOfferEvent, props.ruleIndex, props.pathIndex, offerIndex);
  };

  const addLanding = (landing: { id: string } = { id: '' }) => {
    props.path.landings.push({
      ...landing,
      weight: 100,
    });
  };

  const addNewLanding = () => {
    if (landingsPlanLimits.hasPlanLimitsRestriction('landingsAmount')) {
      return landingsPlanLimits.handleClickPlanLimits('landingsAmount');
    }

    $emitter.emit(flowPathEvents.addLandingEvent);
  };

  const editLanding = (id: string) => {
    $emitter.emit(flowPathEvents.editLandingEvent, id);
  };

  const previewLanding = (url: string) => {
    window.open(url, '_blank');
  };

  const delLanding = (landingIndex: number) => {
    props.path.landings.splice(landingIndex, 1);
    $emitter.emit(flowPathEvents.deleteLandingEvent, props.ruleIndex, props.pathIndex, landingIndex);
  };

  const fixLandingCount = (selectLanding: { offers: number }, preserveLandings = false) => {
    if (!preserveLandings) {
      props.path.landings = [];
      addLanding(selectLanding);
    }

    const deltaOffers = currentOffersCount.value - selectLanding.offers;

    if (deltaOffers < 0) {
      addOffer(Math.abs(deltaOffers));
    } else {
      delOffer(selectLanding.offers, Math.abs(deltaOffers));
    }
  };

  const addItem = () => {
    if (props.type === 'landings') {
      addLanding();
    } else {
      addOffer();
    }
  };

  const createItem = () => {
    if (props.type === 'landings') {
      addNewLanding();
    } else {
      addNewOffer();
    }
  };

  const itemActions = computed(() => ({
    add: {
      key: 'add',
      title: typeSingle.value === 'landing' ? t('Add Landing') : t('Add Offer'),
      disabled: isAddDisabled.value || !noDirect.value,
      click: () => addItem(),
    },
    addNew: {
      key: 'addNew',
      title: typeSingle.value === 'landing' ? t('Add new landing') : t('Add new offer'),
      disabled: !noDirect.value,
      click: () => createItem(),
    },
  }));

  const addMenuList = computed(() => [itemActions.value.add, itemActions.value.addNew]);

  watch(retargetingValue, (value) => {
    if (props.type === 'landings') {
      props.path.retargetingLandings = value;
    } else {
      props.path.retargetingOffers = value;
    }
  });

  watch(redirectDomainId, (value) => {
    if (props.type === 'landings') {
      props.path.redirectDomainId = value;
    } else {
      props.path.redirectDomainIdOffers = value;
    }
  });

  watch(redirectMode, (value) => {
    if (props.type === 'landings') {
      props.path.redirectMode = value;
    } else {
      props.path.redirectModeOffers = value;
    }
  });

  watch(noDirect, (value) => {
    if (props.type === 'landings') {
      props.path.directLinking = !value;
    }
  });

  onMounted(() => {
    if (props.type === 'landings') {
      noDirect.value = !props.path.directLinking;
      redirectMode.value = props.path.redirectMode || '302';
      retargetingValue.value = props.path.retargetingLandings || '';
      redirectDomainId.value = props.path.redirectDomainId || '';
    } else {
      redirectMode.value = props.path.redirectModeOffers || '302';
      retargetingValue.value = props.path.retargetingOffers || '';
      redirectDomainId.value = props.path.redirectDomainIdOffers || '';
    }

    retargeting.value = !!retargetingValue.value;
  });

  return {
    t,
    store,
    virtualScrollBreakpoint,
    redirectMode,
    retargeting,
    noDirect,
    retargetingValue,
    redirectDomainId,
    selections,
    currentOffersCount,
    offersCount,
    pathCardTitle,
    retargetingLabel,
    showPlanLimitsWarningDoubleMeta,
    totalWeightOfLandings,
    totalWeightOfOffers,
    addMenuList,
    fixLandingCount,
    delLanding,
    editLanding,
    previewLanding,
    delOffer,
    editOffer,
    previewOffer,
  };
};
